import { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import { CartLineQuantityStyles } from "@insite/content-library/Widgets/Cart/CartLineQuantity";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { IconPresentationProps } from "@insite/mobius/Icon";
import Eye from "@insite/mobius/Icons/Eye";
import EyeOff from "@insite/mobius/Icons/EyeOff";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldPresentationProps } from "@insite/mobius/TextField";
import { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import mergeProps from "@store/Framework/Common/Utilities/mergeProps";
import { pxToRem, pxToRemArray } from "@store/Framework/Common/Utilities/unitHelper";
import EditFill from "@store/Library/Components/Icons/Edit/EditFill";
import LogOut from "@store/Library/Components/Icons/LogOut";
import PlusCircleFill from "@store/Library/Components/Icons/PlusCircle/PlusCircleFill";
import TrashFill from "@store/Library/Components/Icons/Trash/TrashFill";
import { InfosBoxProps } from "@store/Library/Components/Toolkit/InfosBox";
import { CheckboxPresentationProps } from "@store/MobiusOverrides/Checkbox/Checkbox";
import { FormFieldPresentationProps } from "@store/MobiusOverrides/FormField/FormField";
import { ProductAvailabilityStyles } from "@store/Overrides/Components/ProductAvailability";
import { ProductImageStyles } from "@store/Overrides/Components/ProductImage";
import { ProductPriceStyles } from "@store/Overrides/Components/ProductPrice";
import { gridGapStyles } from "@store/Themes/common/mixins/grid";
import {
    priceSecondarySpacingStyles,
    priceStyles,
    priceUnitOfMeasureStyles,
    priceVatLabelStyles,
} from "@store/Themes/common/mixins/price";
import { spacingStyles } from "@store/Themes/common/mixins/spacing";
import { inlineSeparatorStyles } from "@store/Themes/common/mixins/typography";
import { linkGetActionLinkProps } from "@store/Themes/common/props/link";
import { css } from "styled-components";

export interface ProductLineProps {
    spacer?: InjectableCss;
    container?: GridContainerProps;
    messageGridItem?: GridItemProps;
    messageInfosBox?: InfosBoxProps;
    sortSelectGridItem?: GridItemProps;
    selectedCheckbox?: CheckboxPresentationProps;
    imageGridItem?: GridItemProps;
    image?: ProductImageStyles;
    removeLink?: LinkPresentationProps;
    editNotesLink: LinkPresentationProps;
    addNotesLink: LinkPresentationProps;
    addToCartLink?: LinkPresentationProps;
    addToListLink?: LinkPresentationProps;
    leaveListLink?: LinkPresentationProps;
    hideListLink?: LinkPresentationProps;
    unhideListLink?: LinkPresentationProps;
    viewReplacementLink?: LinkPresentationProps;
    contentGridItem?: GridItemProps;
    contentContainer?: GridContainerProps;
    infoGridItem?: GridItemProps;
    infoContainer?: GridContainerProps;
    descriptionGridItem?: GridItemProps;
    brand?: ProductBrandStyles;
    description?: ProductDescriptionStyles;
    partNumbersGridItem?: GridItemProps;
    partNumbers?: ProductPartNumbersStyles;
    configurationGridItem?: GridItemProps;
    configuration?: TypographyProps;
    availabilityGridItem?: GridItemProps;
    availability?: ProductAvailabilityStyles;
    swatchGridItem?: GridItemProps;
    badgesGridItem?: GridItemProps;
    deliveryScheduleGridItem?: GridItemProps;
    deliveryScheduleLink?: LinkPresentationProps;
    costCodeGridItem?: GridItemProps;
    costCodeLabel?: TypographyPresentationProps;
    costCodeSelect?: SelectPresentationProps;
    costCodeText?: TypographyPresentationProps;
    pricingGridItem?: GridItemProps;
    pricingContainer?: GridContainerProps;
    priceGridItem?: GridItemProps;
    price?: ProductPriceStyles;
    savedPrice?: {
        label?: TypographyPresentationProps;
        wrapper?: InjectableCss;
        unitOfMeasure?: TypographyPresentationProps;
        secondaryWrapper?: InjectableCss;
    };
    promotion?: TypographyProps;
    unitOfMeasureGridItem?: GridItemProps;
    unitOfMeasureContainer?: GridContainerProps;
    unitOfMeasureFieldGridItem?: GridItemProps;
    unitOfMeasureQuantityFieldGridItem?: GridItemProps;
    quantityGridItem?: GridItemProps;
    quantity?: CartLineQuantityStyles;
    subtotalGridItem?: GridItemProps;
    subtotalLabel?: TypographyPresentationProps;
    subtotalValue?: TypographyPresentationProps;
    subtotalSecondValue?: TypographyPresentationProps;
    subtotalVatLabel?: TypographyPresentationProps;
    notesWrapper?: InjectableCss;
    textField?: FormFieldPresentationProps<any>;
}

const descriptionTypographyProps: TypographyPresentationProps = {
    forwardAs: "h3",
    css: css`
        ${({ theme }) => css`
            font-size: ${theme.design.typography.size.large};
            font-weight: ${theme.design.typography.weight.bold};
            line-height: ${theme.design.typography.lineHeight.semi};
        `}
    `,
};

const getLinkProps = (src?: IconPresentationProps["src"]) => {
    const spacing = pxToRem(6);

    return mergeProps<LinkPresentationProps>(linkGetActionLinkProps(src), {
        css: css`
            ${spacingStyles(spacing, spacing)}
            display: flex;
        `,
    });
};

const fullGridItemProps: GridItemProps = {
    width: 12,
    css: css`
        flex-direction: column;
    `,
};

const subtotalValue: TypographyPresentationProps = {
    css: css`
        ${priceStyles()}
        font-weight: ${x => x.theme.design.typography.weight.bold};
    `,
};

const textFieldProps: TextFieldPresentationProps = {
    labelProps: {
        variant: "legend",
        css: css`
            ${x => x.theme.typography.legend.css}
        `,
    },
    sizeVariant: "small",
    cssOverrides: {
        formInputWrapper: css`
            ${x => x.theme.formField.defaultProps?.cssOverrides?.formInputWrapper}
            margin-top: ${pxToRem(3)} !important;
        `,
        inputSelect: css`
            ${x => x.theme.formField.defaultProps?.cssOverrides?.inputSelect}
        `,
    },
};

const borderWidth = pxToRem(1);

export const productLineProps: ProductLineProps = {
    spacer: {
        css: css`
            height: ${borderWidth};
        `,
    },
    container: {
        gap: 0,
        css: css`
            ${x => gridGapStyles(pxToRemArray([20]), [x.theme.design.typography.spacing.base])}
            position: relative;
            /* Store Custom Code - Start */
            padding: ${pxToRem(22, 24)};
            border: ${borderWidth} solid ${getColor("common.border")};
            /* Store Custom Code - End */
            word-break: break-word;
            background-color: ${getColor("common.background")};
            margin-top: ${pxToRem(15)}; /* Store Custom Code */
            margin-bottom: ${pxToRem(15)}; /* Store Custom Code */

            &:first-child {
                padding-top: 0;
                border-top: 0;
                margin-top: 0;
            }

            /* Store Removed :not(body > &) &:last-child */

            body > & /* Dragging ProductLine */ {
                z-index: 2;
                height: auto !important;
                box-shadow: ${x => x.theme.shadows.dragVertical};
            }
        `,
    },
    messageGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    messageInfosBox: {
        size: "small",
        type: "warning",
        extendedStyles: {
            wrapper: {
                css: spacingStyles(pxToRem(1), pxToRem(1)),
            },
        },
    },
    sortSelectGridItem: { width: 12 },
    selectedCheckbox: {
        sizeVariant: "small",
        css: css`
            ${x => x.theme.checkbox.defaultProps?.css as any}
            cursor: pointer;
            ${inlineSeparatorStyles(pxToRem(23), pxToRem(1), "common.border")}

            @media print {
                display: none;
            }
        `,
    },
    imageGridItem: {
        width: [4, 3, 3, 2, 2],
        printWidth: 2,
        css: css`
            display: block;
        `,
    },
    image: {
        linkWrappingImage: {
            css: css`
                max-height: ${pxToRem(100)};
            `,
        },
        wrapper: {
            css: spacingStyles(null, pxToRem(12)),
        },
    },
    addToCartLink: getLinkProps(PlusCircleFill),
    removeLink: getLinkProps(TrashFill),
    addToListLink: getLinkProps(PlusCircleFill),
    leaveListLink: getLinkProps(LogOut),
    hideListLink: getLinkProps(EyeOff),
    unhideListLink: getLinkProps(Eye),
    editNotesLink: getLinkProps(EditFill),
    addNotesLink: getLinkProps(EditFill),
    viewReplacementLink: getLinkProps(),
    contentGridItem: {
        width: [8, 9, 9, 10, 10],
        printWidth: 10,
        css: css`
            flex-direction: column;
        `,
    },
    contentContainer: {
        gap: 0,
        css: css`
            ${gridGapStyles(pxToRemArray([16]), pxToRemArray([12]))}
        `,
    },
    infoGridItem: {
        width: [12, 12, 12, 12, 6],
        printWidth: 6,
    },
    infoContainer: {
        gap: 0,
        css: css`
            ${gridGapStyles([], pxToRemArray([8]))}
        `,
    },
    descriptionGridItem: fullGridItemProps,
    brand: {
        nameText: {
            color: "text.main",
            css: css`
                font-size: ${x => x.theme.design.typography.size.smallest};
                font-weight: ${x => x.theme.design.typography.weight.base};
                text-transform: uppercase;
            `,
        },
    },
    description: {
        productDetailLink: {
            typographyProps: descriptionTypographyProps,
        },
        productDetailDescription: descriptionTypographyProps,
    },
    partNumbersGridItem: fullGridItemProps,
    partNumbers: {
        container: {
            gap: 0,
            css: css`
                ${gridGapStyles([], [pxToRem(3)])}
                font-size: ${x => x.theme.design.typography.size.smaller};
                line-height: ${x => x.theme.design.typography.lineHeight.small};
            `,
        },
    },
    configurationGridItem: fullGridItemProps,
    configuration: {
        color: "text.accent",
        forwardAs: "p",
        css: css`
            ${spacingStyles(null, pxToRem(3))}
            font-size: ${x => x.theme.design.typography.size.smaller};
            line-height: ${x => x.theme.design.typography.lineHeight.small};
        `,
    },
    availabilityGridItem: fullGridItemProps,
    availability: {},
    swatchGridItem: fullGridItemProps,
    badgesGridItem: {
        width: [12, 12, 12, 8, 12],
        css: fullGridItemProps.css,
    },
    deliveryScheduleGridItem: fullGridItemProps,
    deliveryScheduleLink: {},
    costCodeGridItem: fullGridItemProps,
    costCodeLabel: {
        variant: "legend",
    },
    costCodeSelect: {
        sizeVariant: "small",
    },
    costCodeText: {
        css: css`
            line-height: ${x => x.theme.design.typography.lineHeight.semi};
        `,
    },
    pricingGridItem: {
        width: [12, 12, 12, 12, 6],
        printWidth: 6,
    },
    pricingContainer: {
        gap: 0,
        css: css`
            ${gridGapStyles([pxToRem(16)], [pxToRem(8)])}
        `,
    },
    priceGridItem: {
        width: [12, 5, 5, 5, 5],
        printWidth: 5,
        css: css`
            flex-direction: column;
        `,
    },
    price: {
        priceLabelText: {
            variant: "legend",
        },
        wrapper: {
            css: css`
                font-weight: ${x => x.theme.design.typography.weight.base};
            `,
        },
        price: {
            saleLabelText: {
                css: css`
                    display: block;
                    font-size: ${x => x.theme.design.typography.size.smaller};
                `,
            },
        },
    },
    savedPrice: {
        label: {
            variant: "legend",
            css: css`
                display: block;
            `,
        },
        wrapper: {
            css: priceStyles(),
        },
        secondaryWrapper: {
            css: css`
                ${priceStyles()}
                ${priceSecondarySpacingStyles()}
            `,
        },
        unitOfMeasure: {
            css: priceUnitOfMeasureStyles(),
        },
    },
    promotion: {
        italic: true,
        color: "text.accent",
        forwardAs: "p",
        css: css`
            font-size: ${x => x.theme.design.typography.size.tiny};
            line-height: ${x => x.theme.design.typography.lineHeight.small};
            ${spacingStyles(null, pxToRem(3))}
        `,
    },
    unitOfMeasureGridItem: { width: [12, 7, 7, 7, 7], printWidth: 7 },
    unitOfMeasureContainer: { gap: 10 },
    unitOfMeasureFieldGridItem: { width: [12, 8, 8, 8, 8], printWidth: 6 },
    unitOfMeasureQuantityFieldGridItem: {
        width: [12, 4, 4, 4, 4],
        printWidth: 6,
        css: css`
            flex-direction: column;
        `,
    },
    quantityGridItem: {
        width: [4, 3, 3, 3, 3],
    },
    quantity: {
        editableQuantityTextField: mergeProps<CartLineQuantityStyles["editableQuantityTextField"]>(textFieldProps, {
            cssOverrides: {
                inputSelect: css`
                    width: ${pxToRem(55)};
                `,
            },
        }),
    },
    subtotalGridItem: {
        width: [8, 4, 4, 4, 4],
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    subtotalLabel: {
        variant: "legend",
        css: css`
            display: block;
        `,
    },
    subtotalValue,
    subtotalSecondValue: {
        ...subtotalValue,
        css: css`
            ${subtotalValue?.css}
            ${priceSecondarySpacingStyles()}
        `,
    },
    subtotalVatLabel: {
        css: priceVatLabelStyles(),
    },
    notesWrapper: {
        css: css`
            width: 100%;
            ${spacingStyles(pxToRem(12), null)}
        `,
    },
    textField: textFieldProps,
};
